// Plik: src/components/HomeClient.tsx
"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import dynamic from 'next/dynamic';
import KnowledgeSection from './KnowledgeSection';   
import ContactForm from '@/components/ContactForm';

// --- LAZY LOADING WIDGETU ---
const GoogleSearchWidget = dynamic(() => import('./GoogleSearchWidget'), {
  loading: () => <div className="w-full max-w-[800px] h-[300px] bg-white/5 rounded-xl animate-pulse" />, 
  ssr: false 
});

// 1. Magiczna Ramka (BorderBeam)
const BorderBeam = ({ duration = 10, delay = 0 }) => {
  return (
    <div className="absolute inset-0 rounded-xl pointer-events-none overflow-hidden">
        <motion.div
            className="absolute -inset-[100%]"
            animate={{
                rotate: [0, 360],
            }}
            transition={{
                duration: duration,
                repeat: Infinity,
                ease: "linear",
                delay: delay,
                repeatDelay: 0
            }}
            style={{
                background: "conic-gradient(from 0deg at 50% 50%, rgba(255,255,255,1) 0deg, rgba(255,255,255,0.3) 180deg, rgba(255,255,255,1) 360deg)",
                filter: "blur(8px)",
            }}
        />
        <div className="absolute inset-[1px] bg-inherit rounded-xl z-10" />
    </div>
  );
};

// --- GŁÓWNY KOMPONENT KLIENCKI ---

export default function HomeClient() {
  return (
    <main className="bg-[#0b0c0e] w-full min-h-screen overflow-x-hidden relative selection:bg-blue-500/30">
      
      {/* --- TŁO GLOBALNE --- */}
      <div className="fixed top-0 right-0 w-[60vw] h-[80vh] bg-blue-600/10 blur-[120px] pointer-events-none -z-10" />
      <div className="fixed inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none -z-10"></div>

      {/* --- SEKCJA 1: HERO (CZARNA) --- */}
      <section className="relative w-full h-auto min-h-screen pt-28 lg:pt-40 pb-20 lg:pb-32 px-6 flex flex-col justify-center overflow-hidden lg:overflow-visible bg-transparent z-10">
        
        <div className="mx-auto max-w-[1250px] w-full flex flex-col items-start justify-center h-full">
          
            <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="text-3xl sm:text-5xl lg:text-[5.5rem] font-bold tracking-tight text-white leading-[1.1] lg:leading-[0.95] text-left"
            >
              Zadbaj o swoją <br />
              <span className="text-gray-200">wizytówkę w sieci.</span>
            </motion.h1>

            <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                className="mt-4 lg:mt-8 max-w-lg text-sm sm:text-lg text-gray-400 leading-relaxed text-left"
            >
              Webpol Solutions to nowoczesne strony WWW, skuteczne SEO i budowanie wizerunku. 
              Pokaż się klientom z najlepszej strony.
            </motion.p>

            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                className="mt-6 lg:mt-10 flex justify-start w-full"
            >
              <Link 
                href="#oferta"
                className="group relative inline-flex items-center gap-2 lg:gap-3 rounded-full bg-[#EBE7D5] px-6 py-3 lg:px-8 lg:py-4 text-xs lg:text-sm font-bold text-black transition-transform hover:scale-105"
              >
                <div className="absolute -inset-2 bg-orange-500/40 blur-lg rounded-full opacity-100 transition duration-500"></div>
                <span className="relative z-10">ZOBACZ OFERTĘ</span>
                <span className="relative z-10 group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </motion.div>

            {/* --- WIDGET GOOGLE --- */}
            <div className="mt-12 lg:mt-20 w-full relative z-10">
                <div className="absolute -inset-10 bg-blue-600/5 blur-[80px] rounded-full -z-10 opacity-60"></div>
                <GoogleSearchWidget />
            </div>

            <div className="hidden lg:block mt-12 border-t border-white/10 pt-6 w-full max-w-4xl">
                <p className="mb-4 text-xs font-medium text-gray-500 uppercase tracking-widest text-left">
                  Wszystko, czego potrzebujesz, by zaistnieć w sieci:
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-bold text-gray-400">
                  <span className="text-white">Web Design</span>
                  <span className="text-gray-600">•</span>
                  <span className="hover:text-white transition">Development</span>
                  <span className="text-gray-600">•</span>
                  <span className="hover:text-white transition">SEO & Ads</span>
                  <span className="text-gray-600">•</span>
                  <span className="hover:text-white transition">Branding</span>
                  <span className="text-gray-600">•</span>
                  <span className="hover:text-white transition">Social Media</span>
                </div>
            </div>

        </div>
      </section>

      {/* --- SEKCJA 2: OFERTA (ROZBUDOWANA) --- */}
      <section id="oferta" className="bg-[#F2F4F7] py-20 lg:py-32 text-black min-h-screen flex flex-col justify-center relative z-20 overflow-hidden">
        
        {/* TŁO: Siatka dekoracyjna */}
        <div className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" 
             style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
        </div>

        <div className="mx-auto max-w-[1250px] px-6 w-full relative z-10">
          
          {/* NAGŁÓWEK */}
          <div className="mb-12 lg:mb-24 max-w-3xl">
            <span className="text-blue-600 font-bold tracking-widest uppercase text-xs lg:text-sm mb-3 block">
              Nasza Oferta
            </span>
            <h2 className="text-4xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.1]">
              Kompleksowe rozwiązania <br />
              <span className="text-gray-400">dla Twojego wzrostu.</span>
            </h2>
            <p className="mt-6 text-lg text-gray-600 max-w-2xl leading-relaxed">
              Nie jesteśmy kolejną agencją od wszystkiego. Specjalizujemy się w czterech kluczowych obszarach, które realnie wpływają na Twój wynik finansowy.
            </p>
          </div>

          {/* GRID Z KARTAMI */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            
            {/* 1. STRONY WWW (Szeroka) */}
            <div className="md:col-span-2 min-h-[320px] rounded-2xl bg-[#0b0c0e] p-8 lg:p-10 text-white shadow-2xl relative overflow-hidden group hover:shadow-orange-500/10 transition-all duration-500 border border-gray-800">
              {/* Glow effect */}
              <div className="absolute top-0 right-0 p-40 bg-orange-500/10 blur-[120px] rounded-full group-hover:bg-orange-500/20 transition duration-700"></div>
              
              <div className="flex h-full flex-col justify-between relative z-10">
                  <div className="flex justify-between items-start">
                    <div className="space-y-4">
                        <div className="h-14 w-14 rounded-2xl bg-white/10 flex items-center justify-center border border-white/5 text-2xl">
                        🚀
                        </div>
                        <h3 className="text-2xl lg:text-3xl font-bold tracking-tight">Strony WWW</h3>
                    </div>
                    {/* Ikona strzałki w rogu */}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2 group-hover:translate-x-0 duration-300">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400"><path d="M7 17L17 7"/><path d="M7 7h10v10"/></svg>
                    </div>
                  </div>

                  <div className="mt-8">
                      <p className="text-gray-400 text-lg leading-relaxed mb-6 max-w-lg">
                        Tworzymy ultraszybkie serwisy na Next.js. To nie są zwykłe wizytówki, to maszyny sprzedażowe zaprojektowane pod konwersję.
                      </p>
                      
                      {/* Lista funkcji */}
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                              <CheckIcon /> Szybkość ładowania &lt; 1s
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                              <CheckIcon /> CMS (Sanity/Strapi)
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                              <CheckIcon /> Animacje Framer Motion
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                              <CheckIcon /> Pełna responsywność
                          </li>
                      </ul>

                      <Link href="/uslugi/strony-www" className="inline-flex items-center gap-2 text-sm font-bold text-white border-b border-orange-500/50 pb-0.5 hover:border-orange-500 transition-colors">
                        Zobacz szczegóły <span className="transition-transform group-hover:translate-x-1">→</span>
                      </Link>
                  </div>
              </div>
            </div>

            {/* 2. SEO (Wąska) */}
            <div className="md:col-span-1 min-h-[320px] rounded-2xl bg-[#0b0c0e] p-8 lg:p-10 text-white shadow-2xl relative overflow-hidden group hover:shadow-blue-500/10 transition-all duration-500 border border-gray-800">
                <div className="absolute bottom-0 left-0 p-32 bg-blue-600/10 blur-[100px] rounded-full group-hover:bg-blue-600/20 transition duration-700"></div>
                
                <div className="flex h-full flex-col justify-between relative z-10">
                    <div className="space-y-4">
                      <div className="h-14 w-14 rounded-2xl bg-white/10 flex items-center justify-center border border-white/5 text-2xl">
                          🔍
                      </div>
                      <h3 className="text-2xl font-bold text-white tracking-tight">SEO & Audyt</h3>
                    </div>

                    <div className="mt-6">
                        <p className="text-gray-400 text-sm leading-relaxed mb-6">
                          Zdominuj wyniki wyszukiwania. Pozycjonujemy strony tak, by klienci sami Cię znajdowali.
                        </p>
                        <ul className="space-y-2 mb-6">
                            <li className="flex items-center gap-2 text-xs text-gray-400"><CheckIcon color="blue" /> Audyt techniczny</li>
                            <li className="flex items-center gap-2 text-xs text-gray-400"><CheckIcon color="blue" /> Link Building</li>
                            <li className="flex items-center gap-2 text-xs text-gray-400"><CheckIcon color="blue" /> Optymalizacja treści</li>
                        </ul>
                        <Link href="/uslugi/optymalizacja-seo" className="inline-flex items-center gap-2 text-sm font-bold text-white border-b border-blue-500/50 pb-0.5 hover:border-blue-500 transition-colors">
                          Więcej <span className="transition-transform group-hover:translate-x-1">→</span>
                        </Link>
                    </div>
                </div>
            </div>

            {/* 3. TOŻSAMOŚĆ CYFROWA (Szeroka) */}
            <div className="md:col-span-2 min-h-[320px] rounded-2xl bg-[#0b0c0e] p-8 lg:p-10 text-white shadow-2xl relative overflow-hidden group hover:shadow-purple-500/10 transition-all duration-500 border border-gray-800">
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-br from-purple-900/10 to-transparent opacity-0 group-hover:opacity-100 transition duration-700"></div>
               
              <div className="flex h-full flex-col justify-between relative z-10">
                  <div className="flex justify-between items-start">
                    <div className="space-y-4">
                        <div className="h-14 w-14 rounded-2xl bg-white/10 flex items-center justify-center border border-white/5 text-2xl">
                            ✨
                        </div>
                        <h3 className="text-2xl lg:text-3xl font-bold tracking-tight">Tożsamość Cyfrowa</h3>
                    </div>
                  </div>

                  <div className="mt-8">
                        <p className="text-gray-400 text-lg leading-relaxed mb-6 max-w-lg">
                          Spójny wizerunek buduje zaufanie. Projektujemy marki, które zapadają w pamięć od pierwszego wejrzenia.
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                              <CheckIcon color="purple" /> Logo & Księga znaku
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                              <CheckIcon color="purple" /> Social Media Design
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                              <CheckIcon color="purple" /> UI/UX Design
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-300">
                              <CheckIcon color="purple" /> Rebranding
                          </li>
                      </ul>
                        <Link href="/uslugi/pakiet-tozsamosci-cyfrowej" className="inline-flex items-center gap-2 text-sm font-bold text-white border-b border-purple-500/50 pb-0.5 hover:border-purple-500 transition-colors">
                          Zobacz portfolio <span className="transition-transform group-hover:translate-x-1">→</span>
                        </Link>
                    </div>
                </div>
            </div>

             {/* 4. HIGH TICKET (Wąska) */}
             <div className="md:col-span-1 min-h-[320px] rounded-2xl bg-[#0b0c0e] p-8 lg:p-10 text-white shadow-2xl relative overflow-hidden group hover:shadow-green-500/10 transition-all duration-500 border border-gray-800">
                <div className="absolute top-0 right-0 p-24 bg-green-500/10 blur-[80px] rounded-full group-hover:bg-green-500/20 transition duration-700"></div>
                
                <div className="flex h-full flex-col justify-between relative z-10">
                    <div className="space-y-4">
                       <div className="h-14 w-14 rounded-2xl bg-white/10 flex items-center justify-center border border-white/5 text-2xl">
                         💎
                      </div>
                      <h3 className="text-2xl font-bold text-white tracking-tight">High Ticket</h3>
                    </div>

                    <div className="mt-6">
                        <p className="text-gray-400 text-sm leading-relaxed mb-6">
                          Skaluj biznes, docierając do klientów premium. Strategie na wysokie marże.
                        </p>
                        <ul className="space-y-2 mb-6">
                            <li className="flex items-center gap-2 text-xs text-gray-400"><CheckIcon color="green" /> Lejki sprzedażowe</li>
                            <li className="flex items-center gap-2 text-xs text-gray-400"><CheckIcon color="green" /> Cold Calling</li>
                            <li className="flex items-center gap-2 text-xs text-gray-400"><CheckIcon color="green" /> Networking</li>
                            <li className="flex items-center gap-2 text-xs text-gray-400"><CheckIcon color="green" /> Cold Emailing</li>
                        </ul>
                        <Link href="/uslugi/prospekting-high-ticket" className="inline-flex items-center gap-2 text-sm font-bold text-white border-b border-green-500/50 pb-0.5 hover:border-green-500 transition-colors">
                          Więcej <span className="transition-transform group-hover:translate-x-1">→</span>
                        </Link>
                      </div>
                </div>
            </div>

          </div>
        </div>
      </section>


      <ContactForm />



      {/* --- SEKCJA 3: TECHNOLOGIA (GOOGLE STACK) --- */}
<section className="bg-[#0b0c0e] py-16 lg:py-0 lg:min-h-screen flex flex-col justify-center relative overflow-hidden z-30 border-t border-white/5">
  
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-blue-900/10 blur-[150px] pointer-events-none"></div>

  <div className="mx-auto max-w-[1250px] px-6 relative z-10 w-full">
    
    {/* NAGŁÓWEK */}
    <div className="text-left lg:text-center max-w-3xl mx-0 lg:mx-auto mb-10 lg:mb-16">
      <h2 className="text-3xl lg:text-6xl font-bold tracking-tight text-white mb-4">
        Oparte na technologii <br />
        <span className="text-gray-400">Google Cloud.</span>
      </h2>
      <p className="text-sm lg:text-lg text-gray-500 max-w-2xl mx-0 lg:mx-auto">
        Budujemy Twoją obecność w sieci, wykorzystując te same standardy wydajności i bezpieczeństwa, z których korzystają światowi liderzy.
      </p>
    </div>

    {/* CENTRALNY OBRAZ - INFRASTRUKTURA */}
    <div className="relative w-full max-w-5xl mx-auto mb-12 lg:mb-20"> 
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-gradient-to-r from-blue-600/20 to-orange-500/20 blur-[90px] rounded-full -z-10 opacity-50"></div>
      
      <div className="relative aspect-[16/9] lg:aspect-[16/8] w-full rounded-3xl overflow-hidden shadow-2xl bg-[#13151a] border border-white/5">
        <BorderBeam duration={12} delay={0} />
        
        <div className="absolute inset-[1px] bg-[#0b0c0e] rounded-3xl flex items-center justify-center overflow-hidden z-10 group">
          <img 
            src="/image/zdj-google-infrastruktura.png" 
            alt="Google Cloud Infrastructure" 
            className="w-full h-full object-cover opacity-90 transition-transform duration-1000 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none mix-blend-overlay"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c0e] via-transparent to-transparent pointer-events-none"></div>
        </div>
      </div>
    </div>

    {/* TRZY KOLUMNY CECH */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 text-left max-w-5xl mx-auto">
      
      {/* Kolumna 1 */}
      <div className="space-y-3 lg:space-y-4">
        <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500 border border-orange-500/20">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 lg:w-6 lg:h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
          </svg>
        </div>
        <h3 className="text-lg lg:text-xl font-bold text-white">Infrastruktura Firebase</h3>
        <p className="text-gray-500 leading-relaxed text-xs lg:text-sm">
          Twoja strona korzysta z globalnej sieci Google CDN, zapewniając błyskawiczne ładowanie z dowolnego miejsca na świecie.
        </p>
      </div>

      {/* Kolumna 2 */}
      <div className="space-y-3 lg:space-y-4">
        <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 border border-blue-500/20">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 lg:w-6 lg:h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.243-8.25-3.285z" />
          </svg>
        </div>
        <h3 className="text-lg lg:text-xl font-bold text-white">Standardy Bezpieczeństwa</h3>
        <p className="text-gray-500 leading-relaxed text-xs lg:text-sm">
          Gwarantujemy ochronę klasy korporacyjnej dzięki szyfrowaniu SSL i architekturze Google Cloud odpornej na ataki DDoS.
        </p>
      </div>

      {/* Kolumna 3 */}
      <div className="space-y-3 lg:space-y-4">
        <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 border border-green-500/20">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 lg:w-6 lg:h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
        </div>
        <h3 className="text-lg lg:text-xl font-bold text-white">Metodyka Google SEO</h3>
        <p className="text-gray-500 leading-relaxed text-xs lg:text-sm">
          Budujemy witryny zoptymalizowane pod algorytmy Google (Core Web Vitals), co przekłada się na wyższe pozycje w wyszukiwarce.
        </p>
      </div>

    </div>

  </div>
</section>

      {/* --- SEKCJA 4: PREZENTACJA (WIDEO + KNOWLEDGE) --- */}
      <section className="bg-white py-16 lg:py-32 relative overflow-hidden z-20">
        
        {/* NOWOŚĆ: TŁO SIATKA DEKORACYJNA (TAK JAK W OFERCIE) */}
        <div className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" 
             style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
        </div>

        <div className="mx-auto max-w-[1250px] px-6 relative z-10">
          
          {/* NAGŁÓWEK */}
          <div className="text-left lg:text-center max-w-3xl mx-0 lg:mx-auto mb-10 lg:mb-16">
             <span className="text-blue-600 font-bold tracking-widest uppercase text-xs lg:text-sm mb-4 block">
                Filozofia Webpol
             </span>
             <h2 className="text-3xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-4">
               Poznaj nasze <br />
               <span className="text-blue-600">podejście.</span>
             </h2>
             <p className="text-sm lg:text-lg text-gray-600 max-w-2xl mx-0 lg:mx-auto">
               Tworzymy wirtualne przestrzenie dla Twojego biznesu. Zobacz, jak łączymy technologię z designem w jeden spójny system.
             </p>
          </div>

          {/* KONTENER WIDEO */}
          <div className="relative w-full max-w-5xl mx-auto mb-12 lg:mb-20">
              
              {/* Glow za wideo */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-blue-600/20 blur-[80px] rounded-full -z-10 opacity-70"></div>
              
              {/* Player Wideo w Ramce */}
              <div className="relative aspect-video w-full rounded-2xl overflow-hidden shadow-2xl bg-[#0b0c0e] border border-white/5 group">
                  <BorderBeam duration={12} delay={0} />
                  <div className="absolute inset-[1px] bg-[#0b0c0e] rounded-2xl overflow-hidden z-10">
                      <iframe 
                         width="100%" 
                         height="100%" 
                         src="https://www.youtube.com/embed/hnnjATEdjYI" 
                         title="Prezentacja Webpol Solutions" 
                         allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                         allowFullScreen
                         className="absolute inset-0 w-full h-full opacity-90 hover:opacity-100 transition-opacity duration-500"
                      ></iframe>
                  </div>
              </div>
          </div>

          {/* SEKCJON KNOWLEDGE (Wyciągnięta tutaj) */}
          <div className="mt-12">
               <KnowledgeSection />
           </div>

        </div>
      </section>

    </main>
  );
}

// --- Ikona "Ptaszek" ---
function CheckIcon({ color = "orange" }: { color?: string }) {
    const colors: Record<string, string> = {
        orange: "text-orange-500",
        blue: "text-blue-500",
        purple: "text-purple-500",
        green: "text-green-500"
    };
    
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 ${colors[color] || colors.orange}`}>
            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
        </svg>
    );
}